import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";

import AUTH_SERVICES from "../../authService";
import { AUTH_ROUTES, resolveNextStep } from "../../authFlow";
import { patchSignupSession, readSignupSession } from "../../authSession";
import { parseAuthError, preferServerReason } from "../../authErrors";
import { SIGNUP_SCHEMA } from "../../validationSchemas";
import { COUNTRY_OPTIONS, safeCountry } from "../../countryOptions";
import {
  AddressField,
  ContactField,
  DateField,
  SelectField,
  SubmitButton,
  TextField,
} from "../../components/fields";
import {
  defaultOtpChannel,
  detectChannelType,
  normalizeEmail,
  oppositeChannel,
  splitFullName,
  toApiGender,
  toE164,
} from "../../authMappers";

import { latestDobForAge } from "../../age";

import { USAGE_OPTIONS } from "../../../profile/usageOptions";

import "../styles.css";

const GENDERS = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
  { label: "Prefer not to say", value: "OTHERS" },
];

const MAX_DOB = latestDobForAge();

/**
 * The fields, as one static element.
 *
 * Nothing here depends on the parent's state, so it is created once and
 * React skips it on every Formik update; each `FastField` inside re-renders
 * only for its own value.
 */
const Fields = memo(function Fields({ isEmail }) {
  return (
    <>
      <ContactField
        name="contactInput"
        label={isEmail ? "Email address" : "Phone number"}
        isPhone={!isEmail}
        countryOptions={COUNTRY_OPTIONS}
        placeholder={isEmail ? "you@example.com" : "024 000 0167"}
      />

      <TextField
        name="fullName"
        label="Full name"
        placeholder="Ama Mensah"
        autoComplete="name"
        enterKeyHint="next"
      />

      <DateField name="dob" label="Date of birth" max={MAX_DOB} />

      <SelectField name="gender" label="Gender" options={GENDERS} />

      <SelectField name="usage" label="Usage" options={USAGE_OPTIONS} />

      <AddressField
        name="address"
        coordsName="coords"
        label="Address"
        placeholder="Start typing your address"
      />
    </>
  );
});

const SignUp = () => {
  const navigate = useNavigate();

  const session = useMemo(() => readSignupSession(), []);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) navigate(AUTH_ROUTES.getStarted, { replace: true });
  }, [session, navigate]);

  // The second channel is not a choice: it is always the opposite type.
  const secondType = session ? oppositeChannel(session.channelType) : "email";
  const secondIsEmail = secondType === "email";

  const submit = useCallback(
    async (values) => {
      const { firstName, lastName } = splitFullName(values.fullName);

      const secondChannelValue = secondIsEmail
        ? normalizeEmail(values.contactInput)
        : toE164(values.contactInput, values.country);

      const detailsPayload = {
        sessionId: session.sessionId,
        firstName,
        lastName,
        gender: toApiGender(values.gender),
        secondChannelValue,
        otpChannel: defaultOtpChannel(detectChannelType(secondChannelValue)),
      };

      /**
       * Only six fields go to session/details/. Date of birth, usage and
       * address are carried in the draft and spent at Add Photo — dropping
       * the draft here silently discarded them for every new user once
       * already.
       */
      const isPhoneFirst = session.channelType === "phone";

      const profileDraft = {
        firstName,
        lastName,
        phoneNumber: isPhoneFirst ? session.contact : secondChannelValue,
        email: isPhoneFirst ? secondChannelValue : session.contact,
        dateOfBirth: values.dob,
        gender: toApiGender(values.gender),
        usageType: values.usage,
        addressLabel: values.address.trim(),
        addressCoords: values.coords,
        countryCode: values.country,
      };

      setError("");
      setLoading(true);

      patchSignupSession({ detailsPayload, profileDraft });

      try {
        const response = await AUTH_SERVICES.sessionDetails(detailsPayload);

        const decision = await resolveNextStep(response.data, {
          origin: "details",
        });

        navigate(decision.route, {
          replace: true,
          state: {
            stage: decision.stage,
            twoInputs: decision.twoInputs,
            mode: decision.mode,
            legKey: decision.legKey,
          },
        });
      } catch (caught) {
        // Server wording wins: a duplicate contact is 409
        // identity.already.exists, which classifies as UNKNOWN.
        setError(preferServerReason(parseAuthError(caught)));
      } finally {
        setLoading(false);
      }
    },
    [navigate, secondIsEmail, session]
  );

  /**
   * Built once per mount. `session.countryCode` seeds the country so the
   * number the user types is read against the country they already picked on
   * Get Started, and the select lets them change it — without one, a user
   * whose second contact is in another country had no way to be valid.
   */
  const initialValues = useMemo(
    () => ({
      channelType: secondType,
      country: safeCountry(session?.countryCode),
      contactInput: "",
      fullName: "",
      dob: "",
      gender: "",
      usage: "",
      address: "",
      coords: null,
    }),
    [secondType, session]
  );

  if (!session) return null;

  return (
    <div className="auth-page">
      <Formik
        initialValues={initialValues}
        validationSchema={SIGNUP_SCHEMA}
        onSubmit={submit}
      >
        <Form className="auth-screen" noValidate>
          <h1>Get Started</h1>
          <p className="auth-sub">
            Enter your details, it help us keep your account secure.
          </p>

          <Fields isEmail={secondIsEmail} />

          <p className="auth-error" aria-live="polite">
            {error}
          </p>

          <SubmitButton loading={loading}>Continue</SubmitButton>
        </Form>
      </Formik>
    </div>
  );
};

export default SignUp;
